import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import classNames from "classnames";

import { optionsCategory, optionsMedium, optionsOrientation, optionsSize, optionsStyle, optionsSurface } from "./Fields/options";
import { validateForm } from "./Fields/validate";

import { ImageInput } from "./Fields/ImageInput";
import { ImageListInput } from "./Fields/ImageListInput";
import { Status } from "./Fields/Status";

import Currency from "components/Currency";
import { InputCheckbox } from "components/InputCheckbox/InputCheckbox";
import { InputNumber } from "components/InputNumber/InputNumber";
import { InputText } from "components/InputText/InputText";
import { InputTextarea } from "components/InputTextarea/InputTextarea";
import { InputSelect } from "components/InputSelect/InputSelect";

interface ProductProps {
  product: any; // replace 'any' with the actual type of your artist
  userType: string | undefined; // replace 'any' with the actual type of your artist
  onSubmit: (productId: string, values: any) => void;
}

function onKeyDownNumberFloat(event: any, currentValue: string) {
  if (event.key === ".") {
    const data = Number(`${currentValue}.`);
    if (Number.isNaN(data)) {
      event.preventDefault();
      return;
    }
  } else if (event.key !== "Backspace" && !/[0-9]/.test(event.key)) {
    event.preventDefault();
  }
}

function convertData(data: any, userType: string) {
  let newData: any = {
    image: data?.image ?? "",
    images: Array.isArray(data?.images) ? data?.images : [],
    title: data?.title ?? '',
    description: data?.description ?? "",
    orientation: data?.orientation ?? "",
    year: data?.year ?? "",
    category: data?.category ?? "",
    style: data?.style ?? "",
    surface: data?.surface ?? "",
    medium: data?.medium ?? "",
    size: data?.size ?? "",
    width: data?.width ?? "",
    height: data?.height ?? "",
    depth: data.depth ?? "",
    weight: data?.weight ?? "",
    price: data?.price ?? "",
    percentDiscount: !Number.isNaN(Number(data.percentDiscount)) ? Number(data.percentDiscount) : 0,
    enableDiscount: typeof data.enableDiscount === "boolean" ? data.enableDiscount: false,
    isAgree: typeof data.isAgree === "boolean" ? data.isAgree: true,
  };

  if (userType === "gallery") {
    newData.artistName = data?.artistName ?? "";
  }
  
  return newData;
}

export default function Product(props: ProductProps) {
  // Submit form
  const onSubmit = async (values: any) => {
    await props.onSubmit(props.product.id, values);
  };

  return (
    <ProductForm
      key={props.product?.id}
      product={props.product}
      userType={props.userType ?? "artist"}
      onSubmit={onSubmit}
    />
  );
}

interface ProductViewProps {
  product: any;
  userType: string;
  onSubmit: (values: any) => void;
}

function ProductForm({ product, userType, onSubmit }: ProductViewProps) {
  const [data, setData] = useState<any>(product);
  const [errors, setErrors] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (product.status !== data.status || product.productStatus !== data.productStatus) {
      setData(product);
    }
    return () => { };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  const onSubmitForm = async (e: any) => {
    e.preventDefault();
    if (!disabled && !loading) {
      try {
        const dataErrors = validateForm("all", data, errors, userType);
        setErrors(dataErrors);
        if (Object.keys(dataErrors).length < 1) {
          setLoading(true);
          await onSubmit(convertData(data, userType));
          setLoading(false);
          toast.success("Save successfully.");
        }

      } catch (e: any) {
        setLoading(false);
        toast.error(e?.message ?? "Error");
      }
    }
  }

  const updateData = useCallback(
    async (key: string, value: any) => {
      if (key === "image" || key === "images") {
        const oldData = data?.[key];
        try {
          setData({
            ...data,
            [key]: value
          });
          await onSubmit({
            [key]: value,
          });
        } catch (e) {
          setData({
            ...data,
            [key]: oldData
          });
        }
      } else {
        let newData = {
          ...data,
          [key]: value
        };

        if (key === "category") {
          if (data.depth && Number(data.depth.length) > 0) {
            newData["depth"] = "";
          }
          if (data.weight && Number(data.weight.length) > 0) {
            newData["weight"] = "";
          }
        }

        if (key === "enableDiscount") {
          newData["percentDiscount"] = value ? "5" : "0";
        }

        setData(newData);
        setErrors(validateForm(key, newData, errors, userType));
      }
    },
    [data, onSubmit, errors, userType]
  );

  const disabled = useMemo(() => data?.status === "approved", [data]);
  const category = useMemo(() => data?.category, [data]);
  const disabledDepthWeight = useMemo(() => disabled || category !== "category_sculptures", [category, disabled]);

  const enableDiscount = useMemo(
    () => typeof data.enableDiscount === "boolean" ? data.enableDiscount : false,
    [data]);


  const price = useMemo(() => data?.price, [data]);
  const percentDiscount = useMemo(
    () => {
      const value = Number(data?.percentDiscount);
      if (!Number.isNaN(value)) {
        return value;
      }
      return 0;
    },
    [data]);


  const discountedPrice = useMemo(() => {
    const numPrice: number = !Number.isNaN(Number(price)) ? Number(price) : 0;
    const numPercentDiscount = !Number.isNaN(Number(percentDiscount)) ? Number(percentDiscount) : 0;
    if (enableDiscount) {
      return Math.ceil(numPrice * ((100 - numPercentDiscount) / 100));
    }
    return Math.ceil(numPrice);
  }, [price, enableDiscount, percentDiscount]);

  const totalPrice = useMemo(() => {
    return Math.ceil(discountedPrice * 0.67);
  }, [discountedPrice]);

  const isAgree = useMemo(
    () => typeof data.isAgree === "boolean" ? data.isAgree : false,
    [data]);

  return (
    <form className="w-full pb-10" onSubmit={onSubmitForm}>
      <div className="flex items-center mt-10 mb-8">
        <div className="flex-1 flex items-center flex-wrap gap-2 ">
          <h1 className="text-2xl font-medium">Publish Your Shot</h1>
          {disabled && <span className="bg-green-100 text-green-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded dark:bg-green-900 dark:text-green-300">Approved</span>}
        </div>
        {disabled && <Status productStatus={product.productStatus} onSubmit={onSubmit} />}
      </div>
      <div className="flex-1 flex flex-col md:flex-row gap-6">
        <div className="flex-1 flex flex-col gap-6">
          <ImageInput value={data?.image ?? ""} onChange={(value) => updateData("image", value)} disabled={disabled} />
          <ImageListInput value={data?.images ?? ""} onChange={(value) => updateData("images", value)} disabled={disabled} />
        </div>
        <div className="flex-1 flex flex-col gap-6">
          <InputText
            label="Artwork title *"
            value={data?.title ?? ""}
            onChange={(value) => updateData("title", value)}
            disabled={disabled}
            error={errors?.title}
          />
          <InputTextarea
            label="Artwork description *"
            value={data?.description ?? ""}
            onChange={(value) => updateData("description", value)}
            disabled={disabled}
            error={errors?.description}
          />
          {userType === "gallery" && (
            <InputText
              label="Artist name *"
              value={data?.artistName ?? ""}
              onChange={(value) => updateData("artistName", value)}
              disabled={disabled}
              error={errors?.artistName}
            />
          )}
          <InputSelect
            label="Orientation *"
            value={data?.orientation ?? ""}
            onChange={(value) => updateData("orientation", value)}
            disabled={disabled}
            placeholder="Select Orientation"
            options={optionsOrientation}
            error={errors?.orientation}
          />
          <div className="grid grid-cols-2 gap-6">
            <InputText
              label="Year of Art Work *"
              value={data?.year ?? ""}
              onChange={(value) => updateData("year", value)}
              disabled={disabled}
              maxLength={4}
              onKeyDown={(event) => {
                if (event.key !== "Backspace" && !/[0-9]/.test(event.key)) {
                  event.preventDefault();
                }
              }}
              error={errors?.year}
            />
            <InputSelect
              label="Category *"
              value={data?.category ?? ""}
              onChange={(value) => updateData("category", value)}
              disabled={disabled}
              options={optionsCategory}
              error={errors?.category}
            />
          </div>
          <InputSelect
            label="Style *"
            value={data?.style ?? ""}
            onChange={(value) => updateData("style", value)}
            disabled={disabled}
            options={optionsStyle}
            error={errors?.style}
          />
          <InputSelect
            label="Surface *"
            value={data?.surface ?? ""}
            onChange={(value) => updateData("surface", value)}
            disabled={disabled}
            options={optionsSurface}
            error={errors?.surface}
          />
          <InputSelect
            label="Medium *"
            value={data?.medium ?? ""}
            onChange={(value) => updateData("medium", value)}
            disabled={disabled}
            options={optionsMedium}
            error={errors?.medium}
          />
          <div>
            <label className="block mb-2 text-sm font-medium text-gray-900">Size *</label>
            <InputSelect
              value={data?.size ?? ""}
              onChange={(value) => updateData("size", value)}
              disabled={disabled}
              options={optionsSize}
              placeholder="Select size"
              error={errors?.size}
            />
            <div className="grid grid-cols-2 gap-x-6 gap-y-4 mt-4">
              <InputText
                placeholder="Width (In Inches)"
                value={data?.width ?? ""}
                onChange={(value) => updateData("width", value)}
                disabled={disabled}
                onKeyDown={(e) => onKeyDownNumberFloat(e, data?.width ?? "")}
                maxLength={5}
                error={errors?.width}
              />
              <InputText
                placeholder="Height (In Inches)"
                value={data?.height ?? ""}
                onChange={(value) => updateData("height", value)}
                disabled={disabled}
                onKeyDown={(e) => onKeyDownNumberFloat(e, data?.height ?? "")}
                maxLength={5}
                error={errors?.height}
              />
              <InputText
                placeholder="Depth (In Inches)"
                value={data?.depth ?? ""}
                onChange={(value) => updateData("depth", value)}
                disabled={disabledDepthWeight}
                onKeyDown={(e) => onKeyDownNumberFloat(e, data?.depth ?? "")}
                maxLength={5}
                error={errors?.depth}
              />
              <InputText
                placeholder="Weight (Gram)"
                value={data?.weight ?? ""}
                onChange={(value) => updateData("weight", value)}
                disabled={disabledDepthWeight}
                onKeyDown={(e) => onKeyDownNumberFloat(e, data?.weight ?? "")}
                maxLength={5}
                error={errors?.weight}
              />
            </div>
          </div>
          <div>
            <label
              htmlFor="price"
              className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
            >
              Listing Price * (<Currency />)
            </label>
            <div className="grid grid-cols-2 gap-x-6 gap-y-4 mb-3">
              <InputText
                value={data?.price ?? ''}
                onChange={(value) => updateData("price", value)}
                disabled={disabled}
                onKeyDown={(e) => onKeyDownNumberFloat(e, data?.price ?? "")}
                enableCurrency
                error={errors?.price}
              />
              <InputNumber
                value={percentDiscount}
                onChange={(value) => updateData("percentDiscount", value)}
                disabled={disabled || !enableDiscount}
                min={0}
                max={25}
                step={5}
              />
            </div>
            <InputCheckbox
              value={enableDiscount}
              onChange={(value) => updateData("enableDiscount", value)}
              label="I want to discount this work and put it on sale"
              disabled={disabled}
            />
          </div>
          <div>
            <label
              className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
            >
              Discounted Price (<Currency />)
            </label>
            <InputText
              value={discountedPrice > 0 ? `${discountedPrice}` : ""}
              disabled
            />

          </div>
          <div className="">
            <label
              className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
            >
              Amount Receivable (<Currency />)
            </label>

            <div className="relative">
              <div className="absolute inset-y-0 start-0 flex items-center ps-2.5 pointer-events-none">
                <Currency />
              </div>
              <InputText
                value={totalPrice > 0 ? `${totalPrice}` : ""}
                disabled
              />
            </div>
            <span className="block text-xs mt-2 text-gray-500">
              We charge 33% commission. Please ensure you have accounted for shipping charges in your price quotation. Please revise the same, if you need to, before submitting this artwork.
            </span>
          </div>
          <InputCheckbox
            value={isAgree}
            onChange={(value) => updateData("isAgree", value)}
            label="I am the Artist and I own the copyright of this work."
            disabled={disabled}
            error={errors?.isAgree}
          />
          <button
            type="submit"
            className={
              classNames(
                "mt-2 w-full text-white focus:outline-none focus:ring-0 focus:ring-red-300 font-medium rounded-full text-sm px-5 py-2.5 text-center ",
                {
                  "bg-red-700 hover:bg-red-800 dark:bg-red-600 dark:hover:bg-red-700 dark:focus:ring-red-900": !disabled,
                  "bg-red-400 dark:bg-red-500 cursor-not-allowed": disabled,
                },
              )
            }
          >{loading ? (
            <svg aria-hidden="true" role="status" className="inline w-4 h-4 me-3 text-white animate-spin" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="#E5E7EB" />
              <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentColor" />
            </svg>
          ) : "Save"}</button>
        </div>
      </div>
    </form>
  );
}